/* Progressive Web App wiring: service worker, install button, offline bar,
   and the server status pill in the header. */

import { health } from "./api";
import { byId } from "./dom";

/** Chrome/Edge's install event. Not in TypeScript's DOM library yet. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;

  /* When a new worker takes over, reload once so the page never runs half the
     old build: a stale app.js against a new index.html renders markup that
     nothing fills in.

     Only armed when the page is ALREADY controlled. On a first visit,
     clients.claim() also fires controllerchange, and reloading there would be
     a pointless flash. `refreshing` guards against a reload loop. */
  if (navigator.serviceWorker.controller) {
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  }

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((registration) => {
        // Catch an update that landed while the tab was open.
        registration.addEventListener("updatefound", () => {
          const worker = registration.installing;
          worker?.addEventListener("statechange", () => {
            if (worker.state === "installed" && navigator.serviceWorker.controller) {
              worker.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
        return registration.update();
      })
      .catch((error: unknown) => {
        // Not fatal: the app works uninstalled and online.
        console.warn("Service worker registration failed:", error);
      });
  });
}

/* Chrome/Edge fire beforeinstallprompt; we keep it and show our own button.
   iOS Safari never fires it (users install via Share > Add to Home Screen),
   so the button stays hidden there rather than showing a broken one. */
export function wireInstallPrompt(): void {
  let deferred: BeforeInstallPromptEvent | null = null;
  const button = byId<HTMLButtonElement>("btnInstall");

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    button.hidden = false;
  });

  button.addEventListener("click", async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    deferred = null;
    button.hidden = true;
  });

  window.addEventListener("appinstalled", () => {
    deferred = null;
    button.hidden = true;
  });
}

/** Header pill: is the server up, and is OCR available? */
export async function checkHealth(): Promise<void> {
  const dot = byId("statusDot");
  const text = byId("statusText");
  try {
    const h = await health();
    dot.className = h.ocr_available ? "dot ok" : "dot bad";
    text.textContent = h.ocr_available
      ? h.ocr_engine === "textract" ? "OCR ready · Textract" : "OCR ready"
      : "OCR unavailable";
  } catch {
    dot.className = "dot bad";
    text.textContent = "server unreachable";
  }
}

export function wireOfflineBar(): void {
  const bar = byId("offlineBar");
  const sync = (): void => {
    bar.hidden = navigator.onLine;
    if (navigator.onLine) {
      void checkHealth();
    } else {
      byId("statusDot").className = "dot bad";
      byId("statusText").textContent = "offline";
    }
  };
  window.addEventListener("online", sync);
  window.addEventListener("offline", sync);
  sync();
}
